import { Action } from '@/_core/fluxstore/types';
import { Activity, Persona, StorymapState, ViewType, defaultViewOptions } from '../types';
import { useStorymapStore } from '../useModule';
import { mapArrayDtoPropsToCamelCase, mapDtoPropsToCamelCase } from '@/_core/utils/mapDtos';
import { queryExecutor } from '@/_core/cqrs';
import { ActivityModel, PublishedStorymapQuery, PublishedStorymapQueryResult } from '../../api/queries';
import { PublishedStorymapAccessKind } from '../../utils/publishedStorymapRoutes';
import { initTaskRelease, refreshTaskReleases } from '../use/useTaskRelease';
import { SetActiveCardIdByClick } from './cardSelection/setActiveCardIdByClick';

import dateFormat from 'date-fns/format';

const { registerReducer, registerMiddleware, dispatch } = useStorymapStore();

export class InitPublishedStorymap extends Action<StorymapState> {
    public static ACTION_TYPE = 'InitPublishedStorymap';
    public _res: PublishedStorymapQueryResult = null;

    public constructor(
        public accessKind: PublishedStorymapAccessKind,
        public accessKey: string,
        public cardId: string,
        public viewType: 'discovery' | 'planning' | 'backlog' | 'classic' | 'timeline' | 'tracking' | null,
        public trackingReleaseId: string | null = null,
        public isCurrent: () => boolean = () => true
    ) {
        super(InitPublishedStorymap.ACTION_TYPE);
    }
}

const middleware = async (state: StorymapState, action: InitPublishedStorymap): Promise<boolean> => {
    action._res = await queryExecutor.executeQuery(new PublishedStorymapQuery(action.accessKind, action.accessKey), () => ({ isHandled: !action.isCurrent(), result: null }));
    if (!action.isCurrent()) return false;
    document.title = action._res.Name + ' story map - StoriesOnBoard';

    return true;
};

const defultValuesOnFrontendModels = () => ({
    isActive: false,
    activeUsers: [] as string[],
    isMultiselected: false,
    isTitleEditing: false,
    isNew: false,
    isSaving: false,
    isCopied: false,
    isCut: false,
    hasAttachment: false,
    isLinkedToIntegrationTool: false,
    linkedItemKey: '',
    linkedItemTypeId: '',
    linkedItemTypeName: '',
    isWatched: false,
});

const mapActivities = (serverSideModel: ActivityModel[]) => {
    return serverSideModel.map(
        (x) =>
            ({
                ...mapCard(x),
                isCollapsed: false,
            } as Activity)
    );
};

const mapCards = (serverSideModel: any[]) => {
    return serverSideModel.map((x) => mapCard(x));
};

const mapCard = (serverSideModel: any) => {
    return {
        ...mapDtoPropsToCamelCase(serverSideModel),
        ...defultValuesOnFrontendModels(),
        createdAt: new Date(serverSideModel.CreatedAt),
        lastUpdatedAt: serverSideModel.LastUpdatedAt ? new Date(serverSideModel.LastUpdatedAt) : new Date(serverSideModel.CreatedAt),
    };
};

const isAllowedPublishedRecipientViewType = (viewType: ViewType): boolean =>
    viewType === ViewType.Discovery || viewType === ViewType.Planning || viewType === ViewType.Backlog;

const coercePublishedRecipientViewType = (viewType: ViewType): ViewType =>
    isAllowedPublishedRecipientViewType(viewType) ? viewType : ViewType.Discovery;

export const applyPublishedStorymapResult = (
    state: StorymapState,
    res: PublishedStorymapQueryResult,
    accessKind: PublishedStorymapAccessKind,
    accessKey: string,
    cardId: string,
    viewTypeOverride: 'discovery' | 'planning' | 'backlog' | 'classic' | 'timeline' | 'tracking' | null,
    trackingReleaseId: string | null = null
): void => {
    state.id = res.Id;
    state.correlationId = res.Id;
    state.name = res.Name;
    state.slug = res.Slug;
    state.productSlug = '';
    state.estimationUnit = res.EstimationUnit;
    state.aboutBoardDescription = res.AboutBoardDescription || '';
    state.aiData = null;
    state.isTemplate = false;
    state.theme = res.Theme;
    state.activityColor = res.ActivityColor;
    state.taskColor = res.TaskColor;
    state.subtaskColor = res.SubtaskColor;
    state.cardKeyCounter = res.CardKeyCounter;
    state.cardKeyPrefix = res.CardKeyPrefix;
    state.isCardKeyEnabled = res.IsCardKeyEnabled;
    state.isPublishedRecipient = true;
    state.publishedShowUserIdentities = !!res.Gates?.ShowUserIdentities;
    state.publishedShowEstimation = !!res.Gates?.ShowEstimation;
    state.showAboutBoard = !!res.Gates?.ShowAboutBoard;
    state.showReleaseDetails = !!res.Gates?.ShowReleaseDetails;
    state.publishedAccessKind = accessKind;
    state.publishedAccessKey = accessKey;

    let viewType = res.ViewType || ViewType.Discovery;
    if (viewTypeOverride == 'discovery') {
        viewType = ViewType.Discovery;
    } else if (viewTypeOverride == 'planning') {
        viewType = ViewType.Planning;
    } else if (viewTypeOverride == 'backlog') {
        viewType = ViewType.Backlog;
    } else if (viewTypeOverride == 'timeline') {
        viewType = ViewType.Timeline;
    } else if (viewTypeOverride == 'tracking') {
        viewType = ViewType.Tracking;
    } else if (viewTypeOverride == 'classic') {
        viewType = ViewType.Classic;
    }
    viewType = coercePublishedRecipientViewType(viewType);
    state.currentViewType = viewType;

    state.discoveryViewOptions = { ...defaultViewOptions, ...mapDtoPropsToCamelCase(res.DiscoveryViewOptions), isShowAssignee: false };
    state.planningViewOptions = {
        ...defaultViewOptions,
        ...mapDtoPropsToCamelCase(res.PlanningViewOptions),
        isShowAssignee: state.publishedShowUserIdentities && !!res.PlanningViewOptions?.IsShowAssignee,
    };
    state.backlogViewOptions = {
        ...defaultViewOptions,
        ...mapDtoPropsToCamelCase(res.BacklogViewOptions),
        isShowAssignee: state.publishedShowUserIdentities && !!res.BacklogViewOptions?.IsShowAssignee,
    };

    state.prioFrameworkType = res.PrioFrameworkType;
    state.activities = mapActivities(res.Activities || []);
    state.tasks = mapCards(res.Tasks || []);
    state.subtasks = mapCards(res.Subtasks || []);
    state.annotations = mapArrayDtoPropsToCamelCase(res.Annotations || []);
    state.subtaskStatuses = mapArrayDtoPropsToCamelCase(res.SubtaskStatuses || []);
    state.customAiStoryTemplates = [];
    state.customAiStoryTemplateExamples = [];
    state.isSampleStorymap = false;
    state.aiTemplateOverrides = [];

    state.releases = (res.Releases || []).map((x) => ({
        ...mapDtoPropsToCamelCase(x),
        startDate: x.StartDate == null ? null : dateFormat(new Date(x.StartDate), 'yyyy-MM-dd'),
        releaseDate: x.ReleaseDate == null ? null : dateFormat(new Date(x.ReleaseDate), 'yyyy-MM-dd'),
        isPinned: false,
        isCollapsed: false,
        isFocused: false,
    }));

    state.releases.push({
        id: null,
        goal: '',
        isPinned: false,
        isCollapsed: false,
        prefix: '',
        name: 'Unscheduled cards',
        isArchived: false,
        order: 10000,
        isFocused: false,
        isNew: false,
    });

    state.colors = mapArrayDtoPropsToCamelCase(res.Colors || []);
    state.personas = mapArrayDtoPropsToCamelCase(res.Personas || []).map((x: Persona) => ({
        ...x,
        customFields: mapArrayDtoPropsToCamelCase(x.customFields || []),
        personalities: mapArrayDtoPropsToCamelCase(x.personalities || []),
        isNew: false,
    }));
    state.personaPictures = [];
    state.defaultPersonaCustomFields = [];
    state.defaultPersonaPersonalities = [];
    state.canEdit = false;
    state.canAdminister = false;
    state.canCopyPrivateLink = false;
    state.canCopyPrivateReleaseLink = false;
    state.isNextGenPrivateLinkSharingEnabled = false;
    state.canComment = false;
    state.hasFeedbackPortalAccess = false;
    state.isIntegrationEnabled = false;
    state.isImportEnabled = false;
    state.canPushActivityLevel = false;
    state.canPushTaskLevel = false;
    state.hasImportableCards = false;
    state.issueTypeAbbreviations = [];
    state.shouldShowOnboarding = false;
    state.isWorkspaceVisible = false;
    state.canWorkspaceMembersComment = false;
    state.viewersCanCommentOnCards = false;
    state.collaborators = [];
    state.shouldOpenIntegrationTokenExpiredDialog = false;

    if (!state.isInitialized) {
        initTaskRelease();
    } else {
        refreshTaskReleases();
    }

    state.isInitialized = true;
    state.trackingViewState.releaseId = viewType === ViewType.Tracking ? trackingReleaseId : null;

    if (cardId) {
        dispatch(
            new SetActiveCardIdByClick(cardId, {
                expandRelease: true,
                expandActivity: true,
            })
        );
    }
};

const reducer = (state: StorymapState, action: InitPublishedStorymap): void => {
    if (!action.isCurrent()) return;
    applyPublishedStorymapResult(
        state,
        action._res,
        action.accessKind,
        action.accessKey,
        action.cardId,
        action.viewType,
        action.trackingReleaseId
    );
};

//@ts-ignore
registerMiddleware(InitPublishedStorymap.ACTION_TYPE, middleware);

//@ts-ignore
registerReducer(InitPublishedStorymap.ACTION_TYPE, reducer);
